import React from 'react';
import moment from 'moment-jalaali';
import {
  convertDigitToProperFormat,
  isANumber,
  convertNumbersToProperFormat,
} from 'utils/number';
import { IChartDataItem } from 'components/Chart/SurveyChart';
import {
  Badge,
  Cell,
  CornerHeader,
  HeaderColumn,
  HeaderColumnPrint,
  HeaderRow,
  TargetIcon,
  TargetRow,
  TargetValue,
  TRTH,
} from './styles';
import messages from './Messages';
import { Chip } from 'components/AssigneeChip/AssigneeChip';
import { connect, MapDispatchToProps } from 'react-redux';
import {
  generatePercentageColor,
  lightGreenSolid,
  lightGray,
  green,
  black,
  white,
} from '../../utils/style/colors';
import {
  calculateHospitalDegre,
  degreeOptions,
} from '../../utils/etebarbakhshi';
import APopover from '../APopover';
import { ICheckPoint, IReportView } from 'store/survey/surveyTypes';
import {
  cumulativePercentageReady,
  cumulativePercentageReadyFn,
} from 'store/survey/surveySlice';
import { appDispatch } from 'store/store';
import { getLanguageBaseOnTZ } from '../../utils/LanguageLocalStorage';
import HoverIcon from '../HoverIcon';
import { sortBy } from 'lodash';
import Select from '../Select';

interface IDispatchToProps {
  cumulativePercentageReady: ({
    groupId,
    key,
    percentage,
    boundPercentage,
  }: {
    groupId: string;
    key: string;
    percentage: number;
    boundPercentage: number;
  }) => ReturnType<typeof cumulativePercentageReadyFn>;
}

type IOwnProps = {
  checkpoints: ICheckPoint[];
  data: Required<IChartDataItem>[];
  isPrinting: boolean;
  onClickOnCell: (itemId: any, itemId2?: any) => void;
  groupId?: string;
  itemKey?: string;
  series: string[];
  showPercentage: boolean;
  reverse?: boolean;
  reportTitle: string;
  ref?: React.RefObject<HTMLElement>;
  report?: IReportView;
  simple?: boolean;
  compact?: boolean;
  color?: string;
};
type ComponentProps = IOwnProps & IDispatchToProps;
interface IComponentState {
  sortByKey?: string;
  sortOrder?: 'asc' | 'desc';
  degreeFilter?: string;
}

const degreeField = 'درجه بیمارستان';

const HeaderTooltip: React.FC<{
  text: string;
  isPrinting?: boolean;
  children: React.ReactElement;
}> = ({ text, isPrinting, children }) => {
  if (isPrinting || !text) return children;
  return (
    <APopover
      trigger="hover"
      placement="top"
      arrow
      curveBorder
      target={children}
    >
      <span style={{ color: black }}>{text}</span>
    </APopover>
  );
};

class HeatChart extends React.Component<ComponentProps, IComponentState> {
  handleClickOnCell = (item: any, item2?: any) => () => {
    this.props.onClickOnCell(item, item2);
  };

  state = {
    sortByKey: undefined,
    sortOrder: 'asc',
  } as IComponentState;
  render() {
    const {
      checkpoints,
      isPrinting,
      series,
      showPercentage,
      reverse,
      reportTitle,
      report,
      color,
      compact,
    } = this.props;
    let { data } = this.props;
    // let sum = 0;
    // let valueSum = 0;
    // let boundSum = 0;
    // let count = 0;
    let showDegree = false;
    if (reportTitle?.startsWith('درجه بندی')) {
      data.forEach((it: any) => {
        it[degreeField] = calculateHospitalDegre(it);
      });
      data = data.filter(
        (it) =>
          !this.state.degreeFilter ||
          it[degreeField] === this.state.degreeFilter
      );
      if (!series.includes(degreeField)) series.push(degreeField);
      showDegree = true;
    }
    const maxValue = data.reduce((current, item) => {
      const max = Math.max(
        ...Object.entries(item)
          .filter((it) => !it[0].startsWith('__') && isANumber(it[1]))
          .map((it) => it[1] as number)
      );
      if (current > max) return current;
      return max;
    }, Number.MIN_VALUE);
    const greenFind = report?.tags?.find((it) => it.startsWith('green'));
    let greenThreshold: number | undefined = Number(
      greenFind?.substring('green_'.length) || 'NaN'
    );
    if (isNaN(greenThreshold)) greenThreshold = undefined;
    const greenUpsideDown = greenFind?.includes('<') ? -1 : 1;
    const redFind = report?.tags?.find((it) => it.startsWith('red'));
    let redThreshold: number | undefined = Number(
      redFind?.substring('red_'.length) || 'NaN'
    );
    if (isNaN(redThreshold)) redThreshold = undefined;
    const redUpsideDown = redFind?.includes('<') ? -1 : 1;
    const replaceValueWith =
      report?.tags
        .find((it) => it.startsWith('conf_replace_value_with:'))
        ?.split(':')?.[1] || undefined;
    const getSortIcon = (item: Required<IChartDataItem> | string) => {
      if (this.props.simple) return <></>;
      const key = typeof item === 'string' ? item : item.__id.toString();
      return (
        <HoverIcon
          className={
            key !== this.state.sortByKey ? 'notTransparent-on-hover2' : ''
          }
          type={
            this.state.sortOrder === 'asc' || key !== this.state.sortByKey
              ? 'arrow-down'
              : 'arrow-up'
          }
          color={key === this.state.sortByKey ? green : white}
          onClick={() => {
            if (key !== this.state.sortByKey) {
              this.setState({
                sortByKey: key.toString(),
                sortOrder: 'asc',
              });
            } else if (this.state.sortOrder === 'asc')
              this.setState({ sortOrder: 'desc' });
            else
              this.setState({
                sortByKey: undefined,
                sortOrder: 'asc',
              });
          }}
        />
      );
    };

    const backgroundColor = color || lightGreenSolid;
    const degreeFilter = this.state.degreeFilter;
    const rowCount = reverse ? data.length : series.length;
    const tableScrollStyle: React.CSSProperties = {
      position: 'relative',
      overflow: isPrinting ? 'visible' : 'auto',
      width: '100%',
      ...(isPrinting
        ? {}
        : compact
          ? {
              maxHeight: `${(report?.dashboardHeight || 2) * 200 - 80}px`,
            }
          : rowCount > 8
            ? { maxHeight: '75vh' }
            : {}),
    };
    return (
      <div style={tableScrollStyle}>
        <table className="highlightedTable" ref={this.props.ref as any}>
          {!reverse ? (
            <>
              <thead>
                <tr>
                  <CornerHeader>‌</CornerHeader>
                  {data.map((item) => (
                    <HeaderTooltip
                      key={`${item.__id}${Math.random()}`}
                      text={item.__name}
                      isPrinting={isPrinting}
                    >
                      <HeaderRow
                        className={'hover-area2'}
                        bgColor={item.__name === 'کل' ? lightGray : undefined}
                      >
                        <span
                          style={{
                            display: 'flex',
                            justifyContent: this.props.simple
                              ? 'center'
                              : 'space-between',
                            alignItems: 'flex-start',
                            gap: '0.25rem',
                            minWidth: '7rem',
                            maxWidth: '10rem',
                          }}
                        >
                          {item.__name}
                          {getSortIcon(item)}
                        </span>
                      </HeaderRow>
                    </HeaderTooltip>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(this.state.sortByKey
                  ? sortBy(
                      series,
                      (a) =>
                        (this.state.sortOrder === 'desc' ? -1 : 1) *
                        (Number(
                          data.find(
                            (it) => it.__id.toString() === this.state.sortByKey
                          )?.[a]
                        ) || Number.MIN_VALUE)
                    )
                  : series
                ).map((field, index) => (
                  <tr
                    className={'hover-area2'}
                    key={`${field}#${index}${Math.random()}`}
                  >
                    <HeaderTooltip text={field} isPrinting={isPrinting}>
                      {isPrinting ? (
                        <HeaderColumnPrint
                          bgColor={field === 'کل' ? lightGray : undefined}
                        >
                          {field}
                        </HeaderColumnPrint>
                      ) : (
                        <HeaderColumn
                          bgColor={field === 'کل' ? lightGray : undefined}
                        >
                          {field}
                        </HeaderColumn>
                      )}
                    </HeaderTooltip>
                    {data.map((item, index) => {
                      const prevItem = data[index - 1];
                      const value = item[field] as number;
                      const percentage = showPercentage
                        ? (item['__percentageOfRealization' + field] as number)
                        : undefined;
                      const target = item['__target' + field] as number;
                      const secondId = item['__id' + field] as number;
                      let prevPercentage;
                      if (prevItem) {
                        prevPercentage =
                          prevItem['__percentageOfRealization' + field];
                      }
                      return (
                        <Cell
                          key={`${field}#${Math.random()}`}
                          onClick={this.handleClickOnCell(item.__id, secondId)}
                          bgColor={
                            isANumber(greenThreshold) &&
                            isANumber(redThreshold) &&
                            isANumber(value)
                              ? (value - greenThreshold!) * greenUpsideDown > 0
                                ? 'rgba(27, 193, 0, 0.5)'
                                : (value - redThreshold!) * redUpsideDown > 0
                                  ? 'rgba(248, 0, 0, 0.5)'
                                  : 'rgba(255, 225, 71, 0.5)'
                              : item.__name === 'کل' || field === 'کل'
                                ? lightGray
                                : undefined
                          }
                        >
                          <span style={{ position: 'relative', zIndex: 1 }}>
                            {typeof value === 'undefined'
                              ? messages.withoutData
                              : replaceValueWith
                                ? replaceValueWith
                                : isANumber(value)
                                  ? convertNumbersToProperFormat(value, 2) +
                                    (item.__type === 'percent' ? '٪' : '')
                                  : value}
                          </span>
                          {typeof item.__target !== 'undefined'
                            ? isANumber(percentage) && (
                                <APopover
                                  trigger="hover"
                                  arrow
                                  curveBorder
                                  placement="right"
                                  target={
                                    <Badge
                                      bgColor={generatePercentageColor(
                                        percentage!,
                                        prevPercentage,
                                        false
                                        // item['__baseLine'],
                                        // item['__target']
                                      )}
                                    >
                                      {messages.percentageValue(
                                        convertNumbersToProperFormat(
                                          percentage! + 0,
                                          2
                                        ) // to fix show negative ZERO
                                      )}
                                    </Badge>
                                  }
                                >
                                  <span>
                                    {messages.targetFunc(
                                      convertNumbersToProperFormat(target, 2)
                                    )}
                                  </span>
                                </APopover>
                              )
                            : !this.props.simple &&
                              !isANumber(greenThreshold) &&
                              !isANumber(percentage) && (
                                <>
                                  <div
                                    className={'hide-on-hover2'}
                                    style={{
                                      width:
                                        ((value || 0) / maxValue) * 100 + '%',
                                      height: '100%',
                                      backgroundColor: backgroundColor,
                                      position: 'absolute',
                                      top: 0,
                                      zIndex: 0,
                                      left: 0,
                                    }}
                                  ></div>
                                  <div
                                    className={
                                      'visible-on-hover2 hide-on-mobile'
                                    }
                                    style={{
                                      height:
                                        ((value || 0) / maxValue) * 100 + '%',
                                      width: '100%',
                                      backgroundColor: backgroundColor,
                                      position: 'absolute',
                                      bottom: 0,
                                      zIndex: 0,
                                      left: 0,
                                    }}
                                  ></div>
                                </>
                              )}
                        </Cell>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </>
          ) : (
            <>
              <thead>
                <TRTH>
                  <CornerHeader />
                  {series.map((field, idx: number) => {
                    const isDegree = showDegree && field === degreeField;
                    const sortOrFilter = isDegree ? (
                      <Select
                        values={
                          degreeFilter
                            ? [
                                {
                                  label: degreeFilter,
                                  value: degreeFilter,
                                },
                              ]
                            : []
                        }
                        options={degreeOptions.map((it) => ({
                          label: it,
                          value: it,
                        }))}
                        onChangeValues={(it) =>
                          this.setState({
                            degreeFilter: it[0]?.value?.toString(),
                          })
                        }
                      />
                    ) : (
                      getSortIcon(field)
                    );
                    return (
                      <HeaderTooltip
                        key={`${idx}#${field}${Math.random()}`}
                        text={field}
                        isPrinting={isPrinting}
                      >
                        <HeaderRow
                          className={'hover-area2'}
                          bgColor={field === 'کل' ? lightGray : undefined}
                        >
                          <span
                            style={{
                              display: 'flex',
                              justifyContent: this.props.simple
                                ? 'center'
                                : 'space-between',
                              alignItems: 'flex-start',
                              gap: '0.25rem',
                              ...(isDegree ? { flexWrap: 'wrap' } : {}),
                            }}
                          >
                            {field}
                            {sortOrFilter}
                          </span>
                        </HeaderRow>
                      </HeaderTooltip>
                    );
                  })}
                </TRTH>
              </thead>
              <tbody>
                {(this.state.sortByKey
                  ? sortBy(
                      data,
                      (x) =>
                        (this.state.sortOrder === 'desc' ? -1 : 1) *
                        (Number(x[this.state.sortByKey!]) || Number.MIN_VALUE)
                    )
                  : data
                ).map((item, oondex) => (
                  <tr
                    className={'hover-area2'}
                    key={`${item.__name}-${oondex}-${Math.random()}`}
                  >
                    <HeaderTooltip text={item.__name} isPrinting={isPrinting}>
                      {isPrinting ? (
                        <HeaderColumnPrint
                          bgColor={item.__name === 'کل' ? lightGray : undefined}
                        >
                          {item.__name}
                        </HeaderColumnPrint>
                      ) : (
                        <HeaderColumn
                          bgColor={item.__name === 'کل' ? lightGray : undefined}
                        >
                          {item.__name}
                        </HeaderColumn>
                      )}
                    </HeaderTooltip>
                    {series.map((field) => {
                      const prevItem = data[oondex - 1];
                      const value = item[field] as number;
                      // don't show percentage if no checkpoint available
                      const percentage = showPercentage
                        ? item.__percentageOfRealization
                        : undefined;
                      if (isANumber(percentage)) {
                        // sum += percentage!;
                        // valueSum += value;
                        // count += 1;
                      }
                      const secondId = item['__id' + field] as number;
                      let prevPercentage;
                      if (prevItem) {
                        prevPercentage = prevItem.__percentageOfRealization;
                      }
                      return (
                        <Cell
                          key={`${field}#${Math.random()}`}
                          onClick={this.handleClickOnCell(item.__id, secondId)}
                          bgColor={
                            item.__name === 'کل' || field === 'کل'
                              ? lightGray
                              : undefined
                          }
                        >
                          <span style={{ position: 'relative', zIndex: 1 }}>
                            {typeof value === 'undefined'
                              ? messages.withoutData
                              : replaceValueWith
                                ? replaceValueWith
                                : isANumber(value)
                                  ? convertNumbersToProperFormat(value, 2) +
                                    (item.__type === 'percent' ? '٪' : '')
                                  : value}
                          </span>
                          {typeof item.__target !== 'undefined'
                            ? isANumber(percentage) && (
                                <Badge
                                  bgColor={generatePercentageColor(
                                    percentage!,
                                    prevPercentage,
                                    false
                                    // item['__baseLine'],
                                    // item['__target']
                                  )}
                                >
                                  {messages.percentageValue(
                                    convertNumbersToProperFormat(
                                      percentage! + 0,
                                      2
                                    ) // to fix show negative ZERO
                                  )}
                                </Badge>
                              )
                            : !this.props.simple &&
                              !isANumber(percentage) && (
                                <>
                                  <div
                                    className={'hide-on-hover2'}
                                    style={{
                                      width:
                                        ((value || 0) / maxValue) * 100 + '%',
                                      height: '100%',
                                      backgroundColor: backgroundColor,
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      zIndex: 0,
                                    }}
                                  ></div>
                                  <div
                                    className={
                                      'visible-on-hover2 hide-on-mobile'
                                    }
                                    style={{
                                      height:
                                        ((value || 0) / maxValue) * 100 + '%',
                                      width: '100%',
                                      backgroundColor: backgroundColor,
                                      position: 'absolute',
                                      bottom: 0,
                                      left: 0,
                                      zIndex: 0,
                                    }}
                                  ></div>
                                </>
                              )}
                        </Cell>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </>
          )}
        </table>
        <div>
          {checkpoints.map((checkpoint, index) => (
            <TargetRow key={`${index}#${Math.random()}`}>
              <TargetIcon type="target" />
              {messages.untilDate(
                convertDigitToProperFormat(
                  getLanguageBaseOnTZ() === 'fa'
                    ? moment(checkpoint.date as number).format('jD jMMMM jYYYY')
                    : moment(checkpoint.date as number)
                        .locale('en')
                        .format('MMMM D YYYY')
                )
              )}
              <Chip hasProgress={true}>
                <span>{messages.target}</span>
                <TargetValue>
                  {convertDigitToProperFormat(checkpoint.target || 0)}
                </TargetValue>
              </Chip>
              {/*
              {index === 0 &&
                count > 0 &&
                groupId &&
                itemKey &&
                cumulativePercentageReady({
                  groupId: groupId,
                  key: itemKey,
                  percentage: sum / count,
                  boundPercentage: Math.min(100, Math.max(sum / count, 0)),
                }) && (
                  <div style={{ float: 'left' }}>
                    <Chip hasProgress={true}>
                      <span>{messages.average}</span>
                      <TotalPercentageValue>
                        {convertNumbersToProperFormat(valueSum / count, 2)}
                      </TotalPercentageValue>
                    </Chip>
                    <Chip hasProgress={true}>
                      <span>{messages.totalRealization}</span>
                      <TotalPercentageValue
                        bgColor={generatePercentageColor(
                          sum / count,
                          90,
                          false,
                          checkpoint.baseLine || undefined,
                          checkpoint.target || undefined
                        )}
                      >
                        {convertNumbersToProperFormat(sum / count + 0, 2)}%
                      </TotalPercentageValue>
                    </Chip>
                    <Popover
                      trigger="hover"
                      placement="bottom"
                      overlay={
                        <span>{messages.totalRealizationBoundInfo}</span>
                      }
                    >
                      <Chip hasProgress={true}>
                        <span>{messages.totalRealizationBound}</span>
                        <TotalPercentageValue
                          bgColor={generatePercentageColor(
                            Math.min(100, Math.max(sum / count, 0)),
                            90,
                            false,
                            checkpoint.baseLine || undefined,
                            checkpoint.target || undefined
                          )}
                        >
                          {convertNumbersToProperFormat(
                            Math.min(100, Math.max(sum / count, 0)),
                            2
                          )}
                          %
                        </TotalPercentageValue>
                      </Chip>
                    </Popover>
                  </div>
                )}
*/}
            </TargetRow>
          ))}
        </div>
      </div>
    );
  }
}

const mapDispatchToProps: MapDispatchToProps<IDispatchToProps, IOwnProps> = {
  cumulativePercentageReady: (props: {
    groupId: string;
    key: string;
    percentage: number;
    boundPercentage: number;
  }) => appDispatch(cumulativePercentageReady(props)),
};

export default connect(null, mapDispatchToProps)(HeatChart as any) as any;
